import { LOCATION_CHANGED } from '../ducks/routing.duck';
import { convertMoneyToNumber } from '../util/currency';

// Redux action types that mark a real conversion moment.
//
// These are duplicated as string literals rather than imported from the page
// ducks on purpose: this middleware is wired into the store in src/store.js,
// before any page duck is pulled in, and importing them here would give the
// store a second import path into those modules. src/analytics/analytics.test.js
// imports the real constants and asserts they still match these, so a rename
// fails the test run instead of silently switching an event off.
export const CONVERSION_ACTIONS = {
  SIGNUP_SUCCESS: 'app/auth/SIGNUP_SUCCESS',
  MAKE_OFFER_SUCCESS: 'app/MakeOfferPage/MAKE_OFFER_SUCCESS',
  CONFIRM_PAYMENT_SUCCESS: 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS',
  SPECULATE_TRANSACTION_SUCCESS: 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS',
};

// Meta expects a decimal amount in major units ("12.50"), while Sharetribe
// Money carries minor units plus a currency. convertMoneyToNumber throws for
// anything that is not a Money or cannot be represented safely - analytics must
// never be able to break a checkout, so a bad value degrades to no value rather
// than to an exception.
const moneyToEventParams = money => {
  try {
    if (!money) {
      return {};
    }
    return { value: convertMoneyToNumber(money), currency: money.currency };
  } catch (e) {
    return {};
  }
};

// Create a Redux middleware from the given analytics handlers. Each
// handler should have the following methods:
//
// - trackPageView(canonicalPath, previousPath): called when the URL is changed
// - trackEvent(eventName, params): optional, called on conversion moments
export const createMiddleware = handlers => store => {
  // One InitiateCheckout per visit to the checkout page. Speculation re-runs
  // every time the buyer changes dates or quantity, so without this guard a
  // single checkout would report several InitiateCheckout events.
  let initiateCheckoutSent = false;

  // Analytics runs BEFORE next(action) in this middleware, so anything thrown
  // here would stop the action from ever reaching the reducers. Purchase fires
  // during checkout confirmation, so a third-party script having a bad day must
  // not be able to break a payment. Swallow, log, carry on.
  const safely = (label, fn) => {
    try {
      fn();
    } catch (e) {
      console.error(`Analytics handler failed (${label}):`, e); // eslint-disable-line no-console
    }
  };

  const track = (eventName, params) => {
    handlers.forEach(handler => {
      if (typeof handler.trackEvent === 'function') {
        safely(eventName, () => handler.trackEvent(eventName, params));
      }
    });
  };

  return next => action => {
    const { type, payload } = action;

    if (type === LOCATION_CHANGED) {
      const previousPath = store?.getState()?.routing?.currentCanonicalPath;
      const { canonicalPath } = payload;
      handlers.forEach(handler => {
        safely('trackPageView', () => handler.trackPageView(canonicalPath, previousPath));
      });
      // Leaving (or re-entering) any page starts a fresh checkout attempt.
      initiateCheckoutSent = false;
    }

    if (type === CONVERSION_ACTIONS.SPECULATE_TRANSACTION_SUCCESS && !initiateCheckoutSent) {
      initiateCheckoutSent = true;
      track('InitiateCheckout', moneyToEventParams(payload?.transaction?.attributes?.payinTotal));
    }

    if (type === CONVERSION_ACTIONS.CONFIRM_PAYMENT_SUCCESS) {
      // Fires exactly once, at the moment payment is confirmed - not on viewing
      // the order page, which the buyer can revisit any number of times.
      //
      // CONFIRM_PAYMENT_SUCCESS carries only the order id, so the amount comes
      // from the order that INITIATE_ORDER_SUCCESS put in the store, falling
      // back to the speculated one if the real order is somehow absent.
      const checkoutState = store?.getState()?.CheckoutPage;
      const payinTotal =
        checkoutState?.transaction?.attributes?.payinTotal ||
        checkoutState?.speculatedTransaction?.attributes?.payinTotal;
      // eventID lets Meta deduplicate against a server-side Conversions API
      // event for the same order, if one is ever added.
      const orderId = payload?.uuid || payload?.id?.uuid;
      track('Purchase', {
        ...moneyToEventParams(payinTotal),
        ...(orderId ? { eventID: orderId } : {}),
      });
    }

    if (type === CONVERSION_ACTIONS.MAKE_OFFER_SUCCESS) {
      // The offer being sent is the real intent signal in this marketplace -
      // many offers never reach /checkout.
      track('Lead', {});
    }

    if (type === CONVERSION_ACTIONS.SIGNUP_SUCCESS) {
      track('CompleteRegistration', {});
    }

    next(action);
  };
};
